/**
 * Formats a value as Visits (devices * 6 * 3.2 = devices * 19.2)
 * Returns a range string like "14k-16k" or "less than 1000"
 */
export const formatVisits = (devices) => {
  if (!devices || devices === 0) return '0';

  // Calculate visits: devices * 6 * 3.2 = devices * 19.2
  const visits = Math.ceil(devices * 19.2);

  return formatWithRange(visits);
};

/**
 * Formats a value as Visitors (uniques * 6)
 * Returns a range string like "14k-16k" or "less than 1000"
 */
export const formatVisitors = (uniques) => {
  if (!uniques || uniques === 0) return '0';

  // Calculate visitors: uniques * 6
  const visitors = Math.ceil(uniques * 6);

  return formatWithRange(visitors);
};

/**
 * Formats a number with an appropriate range
 * e.g., 15342 -> "14k-16k"
 */
const formatWithRange = (value) => {
  if (value < 1000) {
    return 'less than 1000';
  }

  // Determine range width based on magnitude
  let rangeWidth;
  if (value < 5000) {
    rangeWidth = 1000; // ±500
  } else if (value < 10000) {
    rangeWidth = 2000; // ±1000
  } else if (value < 50000) {
    rangeWidth = 5000; // ±2500
  } else if (value < 100000) {
    rangeWidth = 10000; // ±5000
  } else if (value < 500000) {
    rangeWidth = 20000; // ±10000
  } else if (value < 1000000) {
    rangeWidth = 50000; // ±25000
  } else {
    rangeWidth = 100000; // ±50000
  }

  // Calculate lower and upper bounds
  const lower = Math.floor(value / rangeWidth) * rangeWidth;
  const upper = lower + rangeWidth;

  // Format with k/M suffix
  const lowerStr = formatNumber(lower);
  const upperStr = formatNumber(upper);

  return `${lowerStr}-${upperStr}`;
};

/**
 * Formats a number with k/M suffix
 * e.g., 15000 -> "15k", 1500000 -> "1.5M"
 */
const formatNumber = (value) => {
  if (value >= 1000000) {
    const millions = value / 1000000;
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
  } else if (value >= 1000) {
    const thousands = value / 1000;
    return thousands % 1 === 0 ? `${thousands}k` : `${thousands.toFixed(1)}k`;
  }
  return value.toString();
};

/**
 * Get the label for the metric
 */
export const getMetricLabel = (metric) => {
  switch (metric) {
    case 'devices':
      return 'Visits';
    case 'uniques':
      return 'Visitors';
    default:
      return metric;
  }
};

/**
 * Format a metric value based on its type
 */
export const formatMetricValue = (value, metric) => {
  if (metric === 'devices') {
    return formatVisits(value);
  } else if (metric === 'uniques') {
    return formatVisitors(value);
  }
  return value ? value.toLocaleString() : '0';
};
